// @djsem/shared — Storage refs, retention policy, cleanup, quotas (Prompt 31).
//
// D1 stores metadata only. Large files go in R2 (or local object storage). This module owns
// the ref/metadata schema, the retention policy, and cleanup-candidate logic. It does NOT
// perform actual R2 deletes — those live in the worker/services layer, driven by dry-run
// reports produced here (approved snapshots protected, temporary previews expire, failed
// job artifacts become candidates).

import { type DJSEMValidationResult, toDJSEMValidationResult, type ValidationResult } from "./validation";
import { SeqCounter } from "./runtime";

// ---- contracts --------------------------------------------------------------

/** Lifecycle class of a stored object, driving retention/cleanup and cleanup-protection rules. */
export type DJSEMStorageClass =
  | "source" | "temporary" | "preview" | "generated"
  | "approved" | "exported" | "archived" | "evidence" | "log";

/** The kind of content a stored object holds. */
export type DJSEMStorageObjectType =
  | "audio" | "stem" | "image" | "video"
  | "json" | "xml" | "manifest" | "log" | "archive";

/** Metadata reference to a stored object (D1 metadata; actual bytes live in R2/local storage). */
export interface DJSEMStorageObjectRef {
  id: string;
  organisationId?: string; projectId?: string; ownerUserId?: string;
  storageClass: DJSEMStorageClass;
  objectType: DJSEMStorageObjectType;
  r2Key?: string;
  localPath?: string;
  hash?: string;
  sizeBytes: number;
  contentType?: string;
  createdAt: string;
  expiresAt?: string;
  approved: boolean;
  protectedFromCleanup: boolean;
  warnings: string[];
}

/** Retention rules for a storage class: how long to keep artifacts and what to protect. */
export interface DJSEMRetentionPolicy {
  id: string;
  storageClass: DJSEMStorageClass;
  keepForDays?: number;
  deleteFailedJobArtifactsAfterDays: number;
  deletePreviewsAfterDays: number;
  protectApprovedSnapshots: boolean;
  protectPurchasedMarketplaceAssets: boolean;
}

/** Storage usage breakdown for an organisation/project, produced by {@link DJSEMStorageRefService.calculateProjectUsage}. */
export interface DJSEMStorageUsageReport {
  organisationId?: string;
  projectId?: string;
  totalBytes: number;
  byStorageClass: Record<string, number>;
  byObjectType: Record<string, number>;
  largestObjects: DJSEMStorageObjectRef[];
  warnings: string[];
}

/** A storage object identified as eligible for cleanup, with the reason and size. */
export interface DJSEMCleanupCandidate {
  ref: DJSEMStorageObjectRef;
  reason: string;
  sizeBytes: number;
}

/** Report produced by a cleanup dry-run or execution. */
export interface DJSEMCleanupReport {
  candidates: DJSEMCleanupCandidate[];
  wouldFreeBytes: number;
  protectedCount: number;
  warnings: string[];
  /** Set only by executeCleanup; dry-run reports leave it undefined. */
  deletedIds?: string[];
}

// ---- inputs -----------------------------------------------------------------

const AT = "1970-01-01T00:00:00.000Z";
const _seq = new SeqCounter();

/** Input for {@link DJSEMStorageRefService.createObjectRef}. */
export interface CreateObjectRefInput {
  organisationId?: string; projectId?: string; ownerUserId?: string;
  storageClass: DJSEMStorageClass;
  objectType: DJSEMStorageObjectType;
  r2Key?: string; localPath?: string;
  hash?: string;
  sizeBytes: number;
  contentType?: string;
  createdAt?: string;
  expiresAt?: string;
  approved?: boolean;
  protectedFromCleanup?: boolean;
}

/** Input for {@link DJSEMStorageRefService.markApproved}. */
export interface MarkStorageApprovedInput { ref: DJSEMStorageObjectRef; }
/** Input for {@link DJSEMStorageRefService.markExpired}. */
export interface MarkStorageExpiredInput { ref: DJSEMStorageObjectRef; expiredAt?: string; reason?: string; }
/** Input for {@link DJSEMStorageRefService.calculateProjectUsage}. */
export interface CalculateProjectUsageInput { refs: DJSEMStorageObjectRef[]; organisationId?: string; projectId?: string; quotaBytes?: number; }
/** Input for {@link DJSEMRetentionService.getRetentionPolicy}. */
export interface GetRetentionPolicyInput { storageClass: DJSEMStorageClass; }
/** Input for {@link DJSEMRetentionService.findCleanupCandidates}. */
export interface FindCleanupCandidatesInput {
  refs: DJSEMStorageObjectRef[];
  policiesByClass?: Partial<Record<DJSEMStorageClass, DJSEMRetentionPolicy>>;
  now?: string;
  /** IDs of jobs whose artifacts are eligible for cleanup (failed/cancelled/expired). */
  cleanableJobIds?: Set<string>;
  /** Map from ref.id → jobId, so we can cross-reference cleanable jobs. */
  refToJobId?: Record<string, string>;
}
/** Input for {@link DJSEMRetentionService.runCleanupDryRun}. */
export interface CleanupDryRunInput extends FindCleanupCandidatesInput {}
/** Input for {@link DJSEMRetentionService.executeCleanup}. */
export interface ExecuteCleanupInput {
  candidates: DJSEMCleanupCandidate[];
  /** True to actually execute; false is a safety toggle for callers. */
  confirm: boolean;
  /** Injected deleter — the worker/service provides the real R2 delete. */
  deleteObject?: (ref: DJSEMStorageObjectRef) => Promise<void>;
}

// ---- storage key convention -------------------------------------------------

// Deterministic, non-user-controlled key prefixes. Never use raw uploaded filenames.
// See section 7 of Prompt 31.
/** Input for {@link buildStorageKey}. */
export interface BuildStorageKeyInput {
  organisationId: string;
  projectId: string;
  subpath: string; // e.g. "source/audio/trk_a/obj_1" — must not contain '..' or absolute paths
}

const SAFE_KEY_SEGMENT = /^[A-Za-z0-9_-]{1,128}$/;

/**
 * Build a deterministic, non-user-controlled object storage key from an organisation,
 * project, and subpath. Never uses raw uploaded filenames (Prompt 31 §7).
 * @param input - The organisation id, project id, and storage subpath.
 * @returns The composed storage key.
 * @throws {Error} If the organisation id, project id, or any subpath segment is invalid (empty, `.`, `..`, or fails the safe-segment pattern).
 */
export function buildStorageKey(input: BuildStorageKeyInput): string {
  for (const [name, val] of [["organisationId", input.organisationId], ["projectId", input.projectId]] as const) {
    if (!SAFE_KEY_SEGMENT.test(val)) throw new Error(`Invalid ${name} for storage key.`);
  }
  const parts = input.subpath.split("/");
  for (const p of parts) {
    if (p === "" || p === "." || p === "..") throw new Error("Invalid subpath (empty segment or '..').");
    if (!SAFE_KEY_SEGMENT.test(p)) throw new Error(`Invalid subpath segment '${p}'.`);
  }
  return `org/${input.organisationId}/project/${input.projectId}/${input.subpath}`;
}

// ---- storage ref service ----------------------------------------------------

/** Owns the storage-object-ref schema: creating, validating, approving, expiring refs, and usage reporting. */
export class DJSEMStorageRefService {
  /**
   * Create a new storage object ref.
   * @param input - Storage class, object type, location (r2Key/localPath), size, and metadata.
   * @returns The created ref, with `approved`/`protectedFromCleanup` defaulted from storage class.
   */
  createObjectRef(input: CreateObjectRefInput): DJSEMStorageObjectRef {
    return {
      id: `so_${_seq.next()}_${input.storageClass}`,
      organisationId: input.organisationId, projectId: input.projectId, ownerUserId: input.ownerUserId,
      storageClass: input.storageClass, objectType: input.objectType,
      r2Key: input.r2Key, localPath: input.localPath,
      hash: input.hash, sizeBytes: input.sizeBytes, contentType: input.contentType,
      createdAt: input.createdAt ?? AT, expiresAt: input.expiresAt,
      approved: input.approved ?? (input.storageClass === "approved" || input.storageClass === "source"),
      protectedFromCleanup: input.protectedFromCleanup ?? (input.storageClass === "approved" || input.storageClass === "source" || input.storageClass === "evidence"),
      warnings: [],
    };
  }

  /**
   * Validate a storage object ref's basic invariants (size, location, safe key).
   * @param ref - The ref to validate.
   * @returns The validation result.
   */
  validateObjectRef(ref: DJSEMStorageObjectRef): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    if (ref.sizeBytes < 0) issues.push({ path: "$.sizeBytes", message: "must be >= 0" });
    if (!ref.r2Key && !ref.localPath) issues.push({ path: "$", message: "at least one of r2Key/localPath is required" });
    if (ref.r2Key && (ref.r2Key.includes("..") || ref.r2Key.startsWith("/"))) issues.push({ path: "$.r2Key", message: "must not contain '..' or leading '/'" });
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "storage_object_ref");
  }

  /**
   * Mark a ref approved, which also protects it from cleanup.
   * @param input - The ref to mark approved.
   * @returns A new ref with `approved`/`protectedFromCleanup` set to true.
   */
  markApproved(input: MarkStorageApprovedInput): DJSEMStorageObjectRef {
    return { ...input.ref, approved: true, protectedFromCleanup: true };
  }

  /**
   * Mark a ref expired.
   * @param input - The ref, optional expiry timestamp, and optional reason.
   * @returns A new ref with `expiresAt` set and a warning noting the expiry.
   */
  markExpired(input: MarkStorageExpiredInput): DJSEMStorageObjectRef {
    return {
      ...input.ref,
      expiresAt: input.expiredAt ?? AT,
      warnings: [...input.ref.warnings, `Marked expired${input.reason ? `: ${input.reason}` : ""}.`],
    };
  }

  /**
   * Compute storage usage totals for an organisation/project, broken down by class and
   * type, with the largest objects and quota warnings.
   * @param input - The refs to consider, optional org/project filter, and optional quota.
   * @returns The usage report.
   */
  calculateProjectUsage(input: CalculateProjectUsageInput): DJSEMStorageUsageReport {
    const filtered = input.refs.filter((r) =>
      (!input.organisationId || r.organisationId === input.organisationId) &&
      (!input.projectId || r.projectId === input.projectId));
    const byStorageClass: Record<string, number> = {};
    const byObjectType: Record<string, number> = {};
    let totalBytes = 0;
    for (const r of filtered) {
      totalBytes += r.sizeBytes;
      byStorageClass[r.storageClass] = (byStorageClass[r.storageClass] ?? 0) + r.sizeBytes;
      byObjectType[r.objectType] = (byObjectType[r.objectType] ?? 0) + r.sizeBytes;
    }
    const largestObjects = [...filtered].sort((a, b) => b.sizeBytes - a.sizeBytes).slice(0, 10);
    const warnings: string[] = [];
    if (input.quotaBytes !== undefined) {
      if (totalBytes > input.quotaBytes) warnings.push(`Storage quota exceeded: ${totalBytes} / ${input.quotaBytes} bytes.`);
      else if (totalBytes > input.quotaBytes * 0.9) warnings.push(`Approaching storage quota (${Math.round((totalBytes / input.quotaBytes) * 100)}%).`);
    }
    return { organisationId: input.organisationId, projectId: input.projectId, totalBytes, byStorageClass, byObjectType, largestObjects, warnings };
  }
}

// ---- retention service ------------------------------------------------------

const DEFAULT_POLICIES: Record<DJSEMStorageClass, DJSEMRetentionPolicy> = {
  source: { id: "rp_source", storageClass: "source", deleteFailedJobArtifactsAfterDays: 7, deletePreviewsAfterDays: 30, protectApprovedSnapshots: true, protectPurchasedMarketplaceAssets: true },
  temporary: { id: "rp_temporary", storageClass: "temporary", keepForDays: 1, deleteFailedJobArtifactsAfterDays: 1, deletePreviewsAfterDays: 1, protectApprovedSnapshots: true, protectPurchasedMarketplaceAssets: true },
  preview: { id: "rp_preview", storageClass: "preview", keepForDays: 14, deleteFailedJobArtifactsAfterDays: 3, deletePreviewsAfterDays: 14, protectApprovedSnapshots: true, protectPurchasedMarketplaceAssets: true },
  generated: { id: "rp_generated", storageClass: "generated", keepForDays: 30, deleteFailedJobArtifactsAfterDays: 3, deletePreviewsAfterDays: 14, protectApprovedSnapshots: true, protectPurchasedMarketplaceAssets: true },
  approved: { id: "rp_approved", storageClass: "approved", deleteFailedJobArtifactsAfterDays: 30, deletePreviewsAfterDays: 30, protectApprovedSnapshots: true, protectPurchasedMarketplaceAssets: true },
  exported: { id: "rp_exported", storageClass: "exported", keepForDays: 90, deleteFailedJobArtifactsAfterDays: 7, deletePreviewsAfterDays: 30, protectApprovedSnapshots: true, protectPurchasedMarketplaceAssets: true },
  archived: { id: "rp_archived", storageClass: "archived", deleteFailedJobArtifactsAfterDays: 30, deletePreviewsAfterDays: 30, protectApprovedSnapshots: true, protectPurchasedMarketplaceAssets: true },
  evidence: { id: "rp_evidence", storageClass: "evidence", deleteFailedJobArtifactsAfterDays: 90, deletePreviewsAfterDays: 90, protectApprovedSnapshots: true, protectPurchasedMarketplaceAssets: true },
  log: { id: "rp_log", storageClass: "log", keepForDays: 30, deleteFailedJobArtifactsAfterDays: 7, deletePreviewsAfterDays: 30, protectApprovedSnapshots: true, protectPurchasedMarketplaceAssets: true },
};

function ageDays(from: string, now: string): number {
  const a = Date.parse(from); const b = Date.parse(now);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 0;
  return Math.max(0, (b - a) / (24 * 60 * 60 * 1000));
}

/** Retention policy lookup, cleanup-candidate identification, dry-run reporting, and cleanup execution. Never performs the actual R2 delete — that's driven by the worker from these dry-run reports. */
export class DJSEMRetentionService {
  /**
   * Get the retention policy for a storage class.
   * @param input - The storage class to look up.
   * @returns The default retention policy for that class.
   */
  getRetentionPolicy(input: GetRetentionPolicyInput): DJSEMRetentionPolicy {
    return DEFAULT_POLICIES[input.storageClass];
  }

  /**
   * Identify refs eligible for cleanup. Approved/protected refs are never candidates.
   * @param input - The refs to consider, optional per-class policy overrides, current time, and cleanable-job info.
   * @returns The list of cleanup candidates with reasons.
   */
  findCleanupCandidates(input: FindCleanupCandidatesInput): DJSEMCleanupCandidate[] {
    const now = input.now ?? new Date().toISOString();
    const cleanableJobIds = input.cleanableJobIds ?? new Set<string>();
    const refToJobId = input.refToJobId ?? {};
    const candidates: DJSEMCleanupCandidate[] = [];

    for (const ref of input.refs) {
      // Rule: approved snapshots + protected refs are never candidates. Full stop.
      if (ref.protectedFromCleanup || ref.approved) continue;

      const policy = input.policiesByClass?.[ref.storageClass] ?? DEFAULT_POLICIES[ref.storageClass];

      // Explicit expiresAt takes precedence over class-level policy.
      if (ref.expiresAt && Date.parse(ref.expiresAt) <= Date.parse(now)) {
        candidates.push({ ref, reason: `Explicit expiresAt (${ref.expiresAt}) reached.`, sizeBytes: ref.sizeBytes });
        continue;
      }

      // Failed/cancelled job artifacts age out on the shorter policy.
      const jobId = refToJobId[ref.id];
      if (jobId && cleanableJobIds.has(jobId)) {
        if (ageDays(ref.createdAt, now) >= policy.deleteFailedJobArtifactsAfterDays) {
          candidates.push({ ref, reason: `Failed-job artifact older than ${policy.deleteFailedJobArtifactsAfterDays} days.`, sizeBytes: ref.sizeBytes });
          continue;
        }
      }

      // Class-level expiry.
      if (ref.storageClass === "preview" && ageDays(ref.createdAt, now) >= policy.deletePreviewsAfterDays) {
        candidates.push({ ref, reason: `Preview older than ${policy.deletePreviewsAfterDays} days.`, sizeBytes: ref.sizeBytes });
        continue;
      }
      if (policy.keepForDays !== undefined && ageDays(ref.createdAt, now) >= policy.keepForDays) {
        candidates.push({ ref, reason: `${ref.storageClass} older than ${policy.keepForDays} days.`, sizeBytes: ref.sizeBytes });
      }
    }
    return candidates;
  }

  /**
   * Produce a dry-run cleanup report without deleting anything.
   * @param input - Same input as {@link findCleanupCandidates}.
   * @returns The candidates, bytes that would be freed, and protected count.
   */
  runCleanupDryRun(input: CleanupDryRunInput): DJSEMCleanupReport {
    const candidates = this.findCleanupCandidates(input);
    const protectedCount = input.refs.filter((r) => r.protectedFromCleanup || r.approved).length;
    return {
      candidates, wouldFreeBytes: candidates.reduce((a, c) => a + c.sizeBytes, 0),
      protectedCount, warnings: [],
    };
  }

  /**
   * Execute a cleanup, deleting the given candidates via the injected `deleteObject`
   * (if provided). Refuses to delete protected/approved refs as defense in depth, and
   * is a no-op unless `confirm` is true.
   * @param input - The candidates to delete, a confirm flag, and an optional deleter.
   * @returns The report, including deleted ids and any per-item delete failures as warnings.
   */
  async executeCleanup(input: ExecuteCleanupInput): Promise<DJSEMCleanupReport> {
    if (!input.confirm) {
      return { candidates: input.candidates, wouldFreeBytes: input.candidates.reduce((a, c) => a + c.sizeBytes, 0), protectedCount: 0, warnings: ["executeCleanup called with confirm=false; no delete performed."], deletedIds: [] };
    }
    const deleted: string[] = [];
    const warnings: string[] = [];
    for (const c of input.candidates) {
      if (c.ref.protectedFromCleanup || c.ref.approved) {
        // Defense in depth — candidates should never include these, but check anyway.
        warnings.push(`Refused to delete protected ref ${c.ref.id}.`);
        continue;
      }
      if (input.deleteObject) {
        try { await input.deleteObject(c.ref); } catch (e) {
          warnings.push(`Delete failed for ${c.ref.id}: ${e instanceof Error ? e.message : String(e)}`);
          continue;
        }
      }
      deleted.push(c.ref.id);
    }
    return { candidates: input.candidates, wouldFreeBytes: input.candidates.reduce((a, c) => a + c.sizeBytes, 0), protectedCount: 0, warnings, deletedIds: deleted };
  }
}
